/**
 * Opción A (autoCharge=true): un único POST /subscriptions crea el lote Y cobra en el acto.
 * El resultado del cobro viene en `response.payment`. No se llama a /batch/:id/pay.
 */

import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  useCreateSubscriptionBatch,
  usePaymentMethods,
  useSetPrimaryPaymentMethod,
} from '../hooks/useBilling';
import type {
  BillingPaymentProvider,
  PaymentAttemptResponse,
  SubscriptionBatchItem,
  SubscriptionBatchResponse,
} from '../../application/dto/BillingDTO';
import { getErrorMessage, getValidationDetails, isApiError } from '@shared/infrastructure/http/api.error';
import {
  extractExistingBatchResponseFrom409,
  getSubscriptionCreateErrorMessage,
} from '../../application/subscription-conflict';
import { toast } from 'sonner';

export type SubscribeBatchFormProps = {
  items: SubscriptionBatchItem[];
  couponCode?: string;
  paymentProvider?: BillingPaymentProvider;
  /** Corre cuando el cobro automático incluido en el alta retornó SUCCESS. */
  onSuccess?: (result: { batch: SubscriptionBatchResponse; attempt: PaymentAttemptResponse }) => void;
  className?: string;
};

export function SubscribeBatchForm({
  items,
  couponCode,
  paymentProvider = 'MERCADOPAGO',
  onSuccess,
  className,
}: SubscribeBatchFormProps) {
  const createBatch = useCreateSubscriptionBatch();
  const setPrimary = useSetPrimaryPaymentMethod();
  const { data: paymentMethods = [] } = usePaymentMethods();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const runningRef = useRef(false);
  const isPending = createBatch.isPending || setPrimary.isPending;

  const primaryPm = paymentMethods.find((pm) => pm.isPrimary) ?? paymentMethods[0] ?? null;
  const [selectedPmId, setSelectedPmId] = useState<string | null>(null);

  useEffect(() => {
    if (selectedPmId) return;
    if (paymentMethods.length === 0) return;
    setSelectedPmId(primaryPm?.id ?? paymentMethods[0].id);
  }, [paymentMethods, primaryPm, selectedPmId]);

  const selectedPm =
    paymentMethods.find((pm) => pm.id === selectedPmId) ??
    primaryPm ??
    paymentMethods[0] ??
    null;

  /**
   * Click único de pago:
   *  - Si el usuario eligió otra tarjeta, primero `setPrimary` (el backend cobra la principal).
   *  - `POST /subscriptions` con autoCharge → el lote se crea Y se cobra; el `attempt` viene en `response.payment`.
   *  - Leer `attempt.status` y ramificar.
   */
  const handlePay = async () => {
    if (runningRef.current || isPending) return;
    if (!selectedPm) {
      toast.error('No hay método de pago guardado.');
      return;
    }
    if (items.length === 0) {
      toast.error('Agrega al menos una mascota con plan al contrato.');
      return;
    }
    runningRef.current = true;
    setFieldErrors({});
    try {
      // Asegurar que la tarjeta elegida sea la principal, porque el backend cobra la principal.
      if (!selectedPm.isPrimary) {
        try {
          await setPrimary.mutateAsync(selectedPm.id);
        } catch (err) {
          toast.error(`No se pudo seleccionar la tarjeta: ${getErrorMessage(err)}`);
          return;
        }
      }

      let batch: SubscriptionBatchResponse;
      try {
        batch = await createBatch.mutateAsync({
          items,
          paymentProvider,
          ...(couponCode?.trim() ? { couponCode: couponCode.trim() } : {}),
        });
      } catch (err) {
        const existing = extractExistingBatchResponseFrom409(err);
        if (existing?.payment) {
          // 409 idempotente: el mismo alta + cobro ya se había procesado.
          batch = existing;
        } else {
          const details = getValidationDetails(err);
          if (details) {
            const flat: Record<string, string> = {};
            for (const [k, v] of Object.entries(details)) flat[k] = (v as string[])[0] ?? '';
            setFieldErrors(flat);
            toast.error('Revisa los datos e inténtalo de nuevo.');
          } else {
            toast.error(
              isApiError(err) && err.isForbidden()
                ? getSubscriptionCreateErrorMessage(err)
                : getSubscriptionCreateErrorMessage(err),
            );
          }
          throw err;
        }
      }

      const attempt = batch.payment;
      if (!attempt) {
        toast.error(
          'El backend no devolvió el resultado del cobro. Revisa el estado de la suscripción en Mi cuenta.',
        );
        return;
      }

      const st = String(attempt.status).toUpperCase();
      if (st === 'SUCCESS') {
        toast.success(
          items.length > 1
            ? 'Pago exitoso, tus mascotas quedaron cubiertas'
            : 'Pago exitoso, tu mascota está cubierta',
        );
        onSuccess?.({ batch, attempt });
        return;
      }
      if (st === 'FAILED') {
        toast.error(
          attempt.errorMessage ??
            'Pago rechazado. Tus suscripciones quedaron en PAST_DUE; el sistema reintentará automáticamente.',
        );
        return;
      }
      if (st === 'PENDING') {
        toast.info('Pago en proceso, te notificaremos cuando confirme.');
        return;
      }
      toast.error(`Estado de pago desconocido: ${attempt.status}`);
    } catch (err) {
      if (!isApiError(err) || !err.isConflict()) {
        toast.error(getErrorMessage(err));
      }
    } finally {
      runningRef.current = false;
    }
  };

  const canSubmit = items.length > 0 && !!selectedPm;
  const brand = selectedPm?.cardBrand ?? 'tarjeta';
  const last4 = selectedPm?.lastFour ?? selectedPm?.last4 ?? '****';

  return (
    <div className={className}>
      {(fieldErrors.items ||
        fieldErrors.pet_ids ||
        fieldErrors.petIds ||
        fieldErrors.plan_id ||
        fieldErrors.batch_id) && (
        <p className="text-sm text-red-600 mb-3">
          {fieldErrors.items ??
            fieldErrors.pet_ids ??
            fieldErrors.petIds ??
            fieldErrors.plan_id ??
            fieldErrors.batch_id}
        </p>
      )}

      {paymentMethods.length > 1 && selectedPm && (
        <div className="space-y-2 mb-5">
          <Select
            value={selectedPm.id}
            onValueChange={(v) => setSelectedPmId(v)}
            disabled={isPending}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {paymentMethods.map((pm) => {
                const last = pm.lastFour ?? pm.last4 ?? '****';
                return (
                  <SelectItem key={pm.id} value={pm.id}>
                    {pm.cardBrand ?? 'Tarjeta'} ****{last}
                    {pm.isPrimary ? ' (Principal)' : ''}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      )}

      {selectedPm ? (
        <Button
          type="button"
          className="w-full bg-[#FF6F61] hover:bg-[#FF6F61]/90 text-white"
          onClick={handlePay}
          disabled={isPending || !canSubmit}
        >
          {setPrimary.isPending
            ? 'Preparando tarjeta...'
            : createBatch.isPending
              ? 'Creando contrato y cobrando...'
              : `Pagar con ${brand} ····${last4}`}
        </Button>
      ) : (
        <p className="text-sm text-amber-700 mt-2">No hay método de pago guardado.</p>
      )}
    </div>
  );
}
